import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { EMPTY, switchMap } from 'rxjs';
import { ITrainingLevelCategory } from '@domains/athlete/athlete.model';
import { TrainingLevelStore } from '@domains/athlete/training-level.store';
import { FeatureCatalogStore } from '@domains/feature/feature.store';
import { SportConfigurationStore } from '@domains/feature-configuration/sport-configuration.store';
import { FeatureManagementService } from '@core/services/feature-management.service';
import { TuiAutoFocus } from '@taiga-ui/cdk';
import {
  TuiButton,
  TuiDialogService,
  TuiInput,
  TuiLoader,
  TuiNotification,
  TuiTextfield,
} from '@taiga-ui/core';
import { TUI_CONFIRM, TuiConfirmData, TuiToastService } from '@taiga-ui/kit';

const CUSTOM_ATHLETE_CATEGORIES_FEATURE_SLUG = 'custom_athlete_categories';

@Component({
  selector: 'app-athlete-category-management',
  templateUrl: 'athlete-category-management.html',
  styleUrl: 'athlete-category-management.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    RouterLink,
    TuiAutoFocus,
    TuiButton,
    TuiInput,
    TuiLoader,
    TuiNotification,
    TuiTextfield,
  ],
})
export class AthleteCategoryManagementComponent {
  readonly purchased = input<boolean | null>(null);

  /** Emitted when a row's "View limits" is clicked — parent navigates to the limits section. */
  readonly viewLimits = output<number>();

  private readonly store = inject(TrainingLevelStore);
  private readonly featureCatalogStore = inject(FeatureCatalogStore);
  private readonly featureManagement = inject(FeatureManagementService);
  private readonly dialogs = inject(TuiDialogService);
  private readonly toasts = inject(TuiToastService);
  /**
   * Injected (unused otherwise) purely to kick off its `rxResource` as soon as the categories
   * page loads — by the time a user opens "Manage limits" for any category, the sport list
   * (and this account's first sport) is already cached, so that sub-view can default straight
   * to a selected sport instead of showing its own loading state first.
   */
  private readonly sportConfigStore = inject(SportConfigurationStore);

  protected readonly categories = this.store.categories;
  protected readonly defaultLevels = this.store.defaultLevels;
  protected readonly isLoading = this.store.isLoading;

  /** Public — read by the parent page to enable/disable its "Create category" header action. */
  readonly hasAvailableSlot = this.store.hasAvailableSlot;

  private readonly catalogFeature = computed(() =>
    this.featureCatalogStore.findBySlug(CUSTOM_ATHLETE_CATEGORIES_FEATURE_SLUG),
  );
  protected readonly isFeatureEnabled = computed(
    () =>
      this.purchased() ??
      this.catalogFeature()?.is_enabled ??
      this.featureManagement.isEnabled(CUSTOM_ATHLETE_CATEGORIES_FEATURE_SLUG),
  );

  protected readonly editingId = signal<number | null>(null);
  protected readonly isAdding = signal(false);
  protected readonly saving = signal(false);

  protected readonly nameControl = new FormControl<string>('', {
    nonNullable: true,
    validators: [Validators.required],
  });

  private readonly nameValue = toSignal(this.nameControl.valueChanges, { initialValue: '' });

  readonly isBusy = computed(() => this.isAdding() || this.editingId() !== null);
  protected readonly canSave = computed(
    () => this.nameValue().trim().length > 0 && !this.saving(),
  );

  /** Public — invoked by the parent page's "Create category" header action. */
  requestAdd(): void {
    this.startAdd();
  }

  protected startAdd(): void {
    this.beginAdd('');
  }

  private beginAdd(prefillName: string): void {
    if (this.isBusy() || !this.store.hasAvailableSlot()) return;
    this.editingId.set(null);
    this.isAdding.set(true);
    this.nameControl.setValue(prefillName);
  }

  protected startEdit(row: ITrainingLevelCategory): void {
    if (this.isBusy()) return;
    this.isAdding.set(false);
    this.editingId.set(row.id);
    this.nameControl.setValue(row.name);
  }

  protected cancel(): void {
    this.nameControl.reset('');
    this.isAdding.set(false);
    this.editingId.set(null);
  }

  protected saveNew(): void {
    if (!this.canSave()) return;
    const slotId = this.store.firstAvailableSlotId();
    if (slotId === null) return;

    this.saving.set(true);
    this.store.rename(slotId, this.nameControl.value.trim()).subscribe({
      next: () => {
        this.toasts.open('Category added.', { appearance: 'positive' }).subscribe();
        this.saving.set(false);
        this.cancel();
      },
      error: () => {
        this.toasts.open('Failed to add category.', { appearance: 'negative' }).subscribe();
        this.saving.set(false);
      },
    });
  }

  protected save(row: ITrainingLevelCategory): void {
    if (!this.canSave()) return;
    this.saving.set(true);
    this.store.rename(row.id, this.nameControl.value.trim()).subscribe({
      next: () => {
        this.toasts.open('Category updated.', { appearance: 'positive' }).subscribe();
        this.saving.set(false);
        this.cancel();
      },
      error: () => {
        this.toasts.open('Failed to update category.', { appearance: 'negative' }).subscribe();
        this.saving.set(false);
      },
    });
  }

  protected copyToNewCategory(name: string): void {
    this.beginAdd(`Copied from ${name}`);
  }

  protected onDelete(row: ITrainingLevelCategory): void {
    const data: TuiConfirmData = {
      content: `Do you want to delete the category "${row.name}"?`,
      yes: 'Delete',
      no: 'Cancel',
      appearance: 'destructive',
    };

    this.dialogs
      .open<boolean>(TUI_CONFIRM, { label: 'Delete category', size: 's', data })
      .pipe(switchMap((confirmed) => (confirmed ? this.store.resetToSlot(row) : EMPTY)))
      .subscribe({
        next: () => this.toasts.open('Category deleted.', { appearance: 'positive' }).subscribe(),
        error: () =>
          this.toasts.open('Failed to delete category.', { appearance: 'negative' }).subscribe(),
      });
  }
}
